import React, { useState } from "react";
import { X } from "lucide-react";

interface KeywordSelectorProps {
  selected: string[];
  onChange: (keywords: string[]) => void;
  availableKeywords: string[];
}

export const KeywordSelector = ({
  selected,
  onChange,
  availableKeywords,
}: KeywordSelectorProps) => {
  const [input, setInput] = useState("");

  const toggleKeyword = (keyword: string) => {
    if (selected.includes(keyword)) {
      onChange(selected.filter((k) => k !== keyword));
    } else {
      onChange([...selected, keyword]);
    }
  };

  const removeKeyword = (keyword: string) => {
    onChange(selected.filter((k) => k !== keyword));
  };

  const addCustomKeyword = () => {
    const trimmed = input.trim();
    if (trimmed && !selected.includes(trimmed)) {
      onChange([...selected, trimmed]);
      setInput("");
    }
  };

  return (
    <div className="space-y-3">
      {/* Currently assigned keywords - removable tags */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((kw) => (
            <div
              key={kw}
              className="inline-flex items-center gap-1 bg-red-900 text-white px-3 py-1 rounded text-sm font-medium"
            >
              {kw}
              <button
                onClick={() => removeKeyword(kw)}
                className="ml-1 hover:text-gray-300"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Pre-given keywords */}
      <div className="flex flex-wrap gap-2">
        {availableKeywords.map((kw) => (
          <button
            key={kw}
            onClick={() => toggleKeyword(kw)}
            className={`px-3 py-1 rounded border border-gray-500 text-sm font-medium transition-colors ${
              selected.includes(kw)
                ? "bg-red-900 text-white"
                : "bg-gray-600 text-gray-300 hover:bg-gray-500"
            }`}
          >
            {kw}
          </button>
        ))}
      </div>

      {/* Custom keyword input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && addCustomKeyword()}
          placeholder="Select or type keywords"
          className="flex-1 bg-gray-600 text-white px-3 py-2 rounded border border-gray-500 focus:border-red-500 focus:outline-none"
        />
        <button
          onClick={addCustomKeyword}
          className="bg-red-900 text-white px-4 py-2 rounded hover:bg-red-800"
        >
          Add
        </button>
      </div>
    </div>
  );
};
